/** Alias de clés d’historique vers le champ affiché dans la fiche. */
export const FIELD_ALIASES = {
  'pricing.pricePerDay': 'pricing.basePrice',
  'pricing.price': 'pricing.basePrice',
  categories: 'category',
  type: 'vehicleType',
  item_type: 'vehicleType',
  itemType: 'vehicleType',
  mileage: 'specs.odometer',
  odometer: 'specs.odometer',
};

const MONGO_ID_RE = /^[a-fA-F0-9]{24}$/;

export const isMongoIdString = (value) => typeof value === 'string' && MONGO_ID_RE.test(value.trim());

export const canonicalFieldKey = (key) => {
  if (!key) return key;
  return FIELD_ALIASES[key] || key;
};

const normalizeCompareLabel = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const lookupIdLabel = (id, idLabels) => {
  if (!id || !idLabels) return null;
  const key = String(id).trim();
  return idLabels[key] || idLabels[key.toLowerCase()] || idLabels[key.toUpperCase()] || null;
};

export const formatDiffValue = (value, { currency, idLabels } = {}) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (typeof value === 'number') {
    const formatted = value.toLocaleString('fr-FR');
    return currency ? `${formatted} ${currency}` : formatted;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '—';
    if (isMongoIdString(trimmed)) {
      return lookupIdLabel(trimmed, idLabels) || '—';
    }
    return trimmed;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    const named = value
      .map((item) => {
        const formatted = formatDiffValue(item, { currency, idLabels });
        return formatted === '—' ? null : formatted;
      })
      .filter(Boolean);
    if (named.length) return named.join(', ');
    return `${value.length} élément(s)`;
  }
  if (typeof value === 'object') {
    const human =
      value.name ||
      value.label ||
      value.cityName ||
      value.city ||
      value.title ||
      value.odometer;
    if (human && !isMongoIdString(String(human))) return String(human);
    const objectId = value._id || value.id || value.$oid;
    if (objectId && isMongoIdString(String(objectId))) {
      const mapped = lookupIdLabel(objectId, idLabels);
      if (mapped) return mapped;
    }
    if (value.location != null && (value.price != null || value.price === 0)) {
      const loc = formatDiffValue(value.location, { currency, idLabels });
      const price =
        typeof value.price === 'number'
          ? value.price
          : Number(value.price);
      const priceLabel = Number.isFinite(price) ? `${price} ${currency || 'MAD'}` : String(value.price);
      return loc && loc !== '—' ? `${loc} (${priceLabel})` : priceLabel;
    }
    if (value.policy != null) {
      const policy = formatDiffValue(value.policy, { currency, idLabels });
      return value.percentage != null ? `${policy} (${value.percentage}%)` : policy;
    }
    if (value.value != null) {
      const suffix = value.type === 'PERCENTAGE' ? '%' : currency || '';
      return suffix ? `${value.value} ${suffix}` : String(value.value);
    }
    if (objectId && isMongoIdString(String(objectId))) return '—';
    try {
      return JSON.stringify(value);
    } catch {
      return '—';
    }
  }
  return String(value);
};

const isBlankLabel = (label) => {
  const normalized = normalizeCompareLabel(label);
  return !normalized || normalized === '—' || normalized === '-' || normalized === 'non renseigné';
};

const isNoOpChange = (oldValue, newValue, idLabels) => {
  const oldLabel = formatDiffValue(oldValue, { idLabels });
  const newLabel = formatDiffValue(newValue, { idLabels });
  if (isBlankLabel(oldLabel) && isBlankLabel(newLabel)) return true;
  return normalizeCompareLabel(oldLabel) === normalizeCompareLabel(newLabel);
};

const ingestChange = (map, field, oldValue, newValue, label, idLabels) => {
  const key = canonicalFieldKey(field);
  if (!key || map.has(key)) return;
  if (isNoOpChange(oldValue, newValue, idLabels)) return;
  map.set(key, { field: key, oldValue, newValue, label });
};

/**
 * Dernière modification par champ (révisions les plus récentes en premier).
 * `lastChanges` (legacy) : objet { fieldPath: oldValue }.
 */
export const buildLatestChangeMap = (history = [], lastChanges, idLabels) => {
  const map = new Map();

  const revisions = Array.isArray(history) ? history : [];
  for (const revision of revisions) {
    const changes = revision?.changes || revision?.history || [];
    if (!Array.isArray(changes)) continue;
    for (const change of changes) {
      ingestChange(
        map,
        change.field || change.path || change.key,
        change.oldValue,
        change.newValue,
        change.label,
        idLabels
      );
    }
  }

  if (lastChanges && typeof lastChanges === 'object' && !Array.isArray(lastChanges)) {
    for (const [field, oldValue] of Object.entries(lastChanges)) {
      ingestChange(map, field, oldValue, undefined, undefined, idLabels);
    }
  }

  return map;
};

export const getChangeForField = (map, fieldKey) => {
  if (!map || !fieldKey) return null;
  const candidates = [fieldKey, FIELD_ALIASES[fieldKey], canonicalFieldKey(fieldKey)].filter(Boolean);

  for (const key of candidates) {
    if (map.has(key)) return map.get(key);
  }

  for (const [key, change] of map.entries()) {
    if (key === fieldKey || key.startsWith(`${fieldKey}.`) || fieldKey.startsWith(`${key}.`)) {
      return change;
    }
  }

  return null;
};
